import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { zodResolver } from '@hookform/resolvers/zod';
import { useStore } from '@nanostores/react';
import { Loader2 } from 'lucide-react';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

const formSchema = z.object({
  workflowName: z.string().min(2, {
    message: 'Workflow name must be at least 2 characters.',
  }),
});

interface Props {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export function NewWorkflowDrawer({ open, setOpen }: Props) {
  const org = useStore($org);
  const ws = useStore($workspace);
  const user = useStore($user);

  // 1. Define your form.
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      workflowName: '',
    },
  });

  // 2. Define a submit handler.
  const [isLoading, setIsLoading] = useState(false);
  async function onSubmit(values: z.infer<typeof formSchema>) {
    setIsLoading(true);
    try {
      if (org && ws && ws.workspace && ws.workspace.id) {
        const workflowResult = await createWorkflow(
          values.workflowName,
          org?.id,
          ws.workspace.id,
          false,
          []
        );

        // route to the workflow builder
        $router.open(
          `/organization/${org!.id}/workspace/${ws.workspace.id}/workflows/${workflowResult?.workflow?.id}`,
        );
      }
    } catch (error) {
      toast('Error creating new project:', {
        description: '',
        duration: 5000,
      });
    }
    setIsLoading(false);
  }

  return (
    <Sheet
      open={open}
      onOpenChange={o => {
        form.reset();
        setOpen(o);
      }}
    >
      <SheetContent className="overflow-y-auto sm:max-w-[600px]">
        <SheetHeader>
          <SheetTitle>
            Create new Workflow
            {/* <Badge className="mb-0">BETA</Badge> */}
          </SheetTitle>
          {/* <SheetDescription>
            Create a project for each file that you want to process.
          </SheetDescription> */}
        </SheetHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="mt-4 space-y-8"
          >
            <FormField
              control={form.control}
              name="workflowName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Workflow name</FormLabel>
                  <FormControl>
                    <Input placeholder="Q1 Call Sync Workflow" {...field} />
                  </FormControl>
                  <FormDescription>
                    Enter a workflow name like "Q1 Call Sync Workflow" to help
                    identify this workflow.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <SheetFooter>
              <SheetClose asChild>
                <Button variant="outline">Cancel</Button>
              </SheetClose>
              <Button type="submit" disabled={isLoading}>
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Submit
              </Button>
            </SheetFooter>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
}
